<app-banner [links]="bannnerLinks"></app-banner>

<div class="container mx-auto px-4 py-8">
  @if (loading) {
  <div class="flex justify-center items-center min-h-[400px]">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
  } @else if (error) {
  <div class="text-red-500 text-center">{{ error }}</div>
  } @else if (structure) {
  <!-- Structure Info Header -->
  <div class="bg-white rounded-lg p-4 mb-6 flex items-start gap-4">
    <!-- Avatar -->
    <div class="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
      <lucide-icon [img]="BuildingIcon" color="gray" [size]="18"></lucide-icon>
    </div>

    <!-- Structure Info -->
    <div class="flex-1">
      <div class="flex flex-wrap items-center gap-2">
        <h4 class="text-[14px] font-medium text-gray-900">
          {{ structure?.name }}
        </h4>

        <!-- Prestataire Connecté indicator -->
        @if (structure.eyoneUser) {
        <div class="flex items-center gap-1"
          [title]="'Ce prestataire échange automatiquement vos informations avec votre {{ getProductName() }} pour faciliter votre prise en charge.'">
          <span
            class="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap">
            <lucide-icon [img]="CircleIcon" [size]="10" class="fill-green-500 text-green-500"></lucide-icon>
            Prestataire Connecté
          </span>
        </div>
        }
      </div>

      <p class="text-primary text-[13px] mt-1">
        {{ structure.structureType?.name }}
      </p>

      <!-- Add Languages section -->
      @if (structure?.languages?.length > 0) {
      <div class="flex items-center gap-2 mt-1">
        <span class="text-[13px] text-gray-600 font-medium">Langues:</span>
        <p class="text-[13px] text-gray-600">
          @for (lang of structure.languages; track $index; let last = $last) {
          {{ lang.name }}@if (!last) {, } }
        </p>
      </div>
      }

      <!-- Contact Info -->
      <div class="mt-3 space-y-1.5">
        @if (structure.coordonate?.email) {
        <div class="flex items-center gap-2 text-gray-600">
          <lucide-icon [img]="MailIcon" color="gray" [size]="14"></lucide-icon>
          <span class="text-[13px]">{{ structure.coordonate.email }}</span>
        </div>
        } @if (structure.coordonate?.mobileNumber ||
        structure.coordonate?.fixNumber) {
        <div class="flex items-center gap-2 text-gray-600">
          <lucide-icon [img]="PhoneIcon" color="gray" [size]="14"></lucide-icon>
          <span class="text-[13px]">
            @if (structure.coordonate?.mobileNumber) {
            {{ structure.coordonate.mobileNumber }}
            } @if (structure.coordonate?.mobileNumber &&
            structure.coordonate?.fixNumber) { &nbsp;/&nbsp; } @if
            (structure.coordonate?.fixNumber) {
            {{ structure.coordonate.fixNumber }}
            }
          </span>
        </div>
        }
      </div>
    </div>
  </div>

  <!-- Details Grid -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
    <!-- Left Column - Details -->
    <div class="lg:col-span-2 space-y-6">

      <!-- Specialisations -->
      <div class="bg-white rounded-lg p-4">
        <h5 class="text-[13px] font-medium text-primary mb-3">
          Spécialisations
        </h5>
        @if (structure.specialisations?.length) {
        <div class="flex flex-wrap gap-2">
          @for (spec of structure.specialisations; track spec.id) {
          <span
            class="bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/20 hover:bg-primary/20 transition-colors">
            {{ spec.name }}
          </span>
          }
        </div>
        } @else {
        <div class="text-gray-400 text-[13px] italic">
          Aucune spécialisation disponible
        </div>
        }
      </div>

      <!-- Practice Locations -->
      <div class="bg-white rounded-lg p-4">
        <h5 class="text-[13px] font-medium text-primary mb-3">
          Les professionnels de santé
        </h5>
        @if (structure.doctors?.length) {
        <div class="space-y-1.5">
          @for (doctor of structure.doctors; track doctor.id) {
          <div class="text-gray-600 text-[13px]">{{ doctor.name }}</div>
          }
        </div>
        } @else {
        <div class="text-gray-400 text-[13px] italic">
          Aucune information disponible
        </div>
        }
      </div>

      <!-- Insurance & Payment -->
      <div class="bg-white rounded-lg p-6">
        <div class="grid grid-cols-2 gap-6">
          <!-- Insurers -->
          <div>
            <h5 class="text-[13px] font-medium text-primary mb-3">
              Organismes de remboursement acceptés
            </h5>
            @if (structure.insurers?.length) {
            <div class="space-y-1.5">
              @for (insurer of structure.insurers; track insurer.id) {
              <div class="text-gray-600 text-[13px]">{{ insurer.name }}</div>
              }
            </div>
            } @else {
            <div class="text-gray-400 text-[13px] italic">
              Aucune information disponible
            </div>
            }
          </div>

          <!-- Payment Methods -->
          <div>
            <h5 class="text-[13px] font-medium text-primary mb-3">
              Moyens de paiement acceptés
            </h5>
            @if (structure.paymentTypes?.length) {
            <div class="space-y-1.5">
              @for (payment of structure.paymentTypes; track payment.id) {
              <div class="text-gray-600 text-[13px]">{{ payment.name }}</div>
              }
            </div>
            } @else {
            <div class="text-gray-400 text-[13px] italic">
              Aucune information disponible
            </div>
            }
          </div>

          <!-- Actes médicaux pratiqués -->
          <div>
            <h5 class="text-[13px] font-medium text-primary mb-3">
              Actes médicaux pratiqués
            </h5>
            @if (structure.medicalActs?.length) {
            <div class="space-y-1.5">
              @for (medicalAct of structure.medicalActs; track medicalAct.id) {
              <div class="text-gray-600 text-[13px]">{{ medicalAct.name }}</div>
              }
            </div>
            } @else {
            <div class="text-gray-400 text-[13px] italic">
              Aucune information disponible
            </div>
            }
          </div>

          <!-- Motifs de rendez-vous -->
          <div>
            <h5 class="text-[13px] font-medium text-primary mb-3">
              Motifs de rendez-vous
            </h5>
            @if (structure.medicalStructureReasons?.length) {
            <div class="space-y-1.5">
              @for (reason of structure.medicalStructureReasons; track
              reason.id) {
              <div class="text-gray-600 text-[13px]">{{ reason.name }}</div>
              }
            </div>
            } @else {
            <div class="text-gray-400 text-[13px] italic">
              Aucune information disponible
            </div>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - Map -->
    <div class="lg:col-span-1">
      <div class="bg-white rounded-lg p-6">
        <h5 class="text-md font-medium text-primary mb-3">
          Adresse et localisation
        </h5>
        @if (structure.coordonate?.globalAddressField) {
        <p class="text-gray-600 mb-4">
          {{ structure.coordonate.globalAddressField }}
        </p>
        }
        <div class="h-[300px] rounded-lg overflow-hidden">
          <app-safe-maps [latitude]="structure.coordonate?.latitude ?? 0"
            [longitude]="structure.coordonate?.longitude ?? 0" [zoom]="15">
          </app-safe-maps>
        </div>
      </div>
    </div>
  </div>
  }
</div>